import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@zg-rentals/react-query5';

import { baseUrl } from './apiConstants';
import { Lease, LeaseAction, Property, Section } from '../models';
import type { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import {
  LeaseDocumentUploadWebApiHandler_getUploadedFileDetails,
  LeaseOuidWebApiHandler_getLeaseFromOuid,
  LeaseRenewalOuidWebApiHandler_getLeaseExtensionFromOuid,
  LeaseRenewalWebApiHandler_getLeaseExtension,
  LeaseSummaryOuidWebApiHandler_getLeaseSummaryFromOuid,
  LeaseSummaryWebApiHandler_getLeaseSummary,
  LeaseWizardWebApiHandler_getFirstNodeInSectionCategory,
  LeaseWizardWebApiHandler_getNextNode,
  LeaseWizardWebApiHandler_getPreviousNode,
  LeasingGetCosmosJourneyTokenWebApiHandler_getJourneyToken,
  LeasingGetCosmosJourneyTokenWebApiHandler_getJourneyTokenFromOuid,
  LeasingPropertyWebApiHandler_getPropertyInfo,
  LeasingUserSigningOuidWebApiHandler_whoHasSignedFromOuid,
  LeasingUserSigningWebApiHandler_whoHasSigned,
} from '@zg-rentals/ts-leasing-client';
import {
  useApplicantsWebApiHandler_getApplicantsForPropertyApplicationId,
  useCloneLeaseWebApiHandler_cloneLease,
  useGeneratedLeaseWebApiHandler_saveFeedback,
  useLeaseCompletionWebApiHandler_getCompletionStatus,
  useLeaseDisclaimerAcceptanceFromOuidWebApiHandler_getAttachedLeaseAcceptanceForUserFromOuid,
  useLeaseDisclaimerAcceptanceFromOuidWebApiHandler_getDisclaimerAcceptanceForUserFromOuid,
  useLeaseDisclaimerAcceptanceFromOuidWebApiHandler_setAttachedLeaseAcceptanceForUserFromOuid,
  useLeaseDisclaimerAcceptanceFromOuidWebApiHandler_setDisclaimerAcceptanceForUserFromOuid,
  useLeaseDisclaimerAcceptanceWebApiHandler_getAttachedLeaseAcceptanceForUser,
  useLeaseDisclaimerAcceptanceWebApiHandler_getDisclaimerAcceptanceForUser,
  useLeaseDisclaimerAcceptanceWebApiHandler_setAttachedLeaseAcceptanceForUser,
  useLeaseDisclaimerAcceptanceWebApiHandler_setDisclaimerAcceptanceForUser,
  useLeaseDocumentsOuidWebApiHandler_getExplainerLeaseFromOuid,
  useLeaseDocumentsOuidWebApiHandler_getLandlordProvidedDocumentsFromOuid,
  useLeaseDocumentsOuidWebApiHandler_previewUrlFromOuid,
  useLeaseDocumentsWebApiHandler_getExplainerLeaseForLease,
  useLeaseDocumentsWebApiHandler_getLandlordProvidedDocuments,
  useLeaseDocumentsWebApiHandler_previewUrl,
  useLeaseDocumentUploadWebApiHandler_deleteLeaseDocument,
  useLeaseDocumentUploadWebApiHandler_getZDocsUploadToken,
  useLeaseDocumentUploadWebApiHandler_renameLeaseDocuments,
  useLeaseDocumentUploadWebApiHandler_reorderLeaseDocuments,
  useLeaseDocumentUploadWebApiHandler_storeDocumentId,
  useLeaseDocumentUploadWebApiHandler_submitDocumentChanges,
  useLeaseRenewalWebApiHandler_create,
  useLeaseSigningWebApiHandler_cancelSigning,
  useLeaseSigningWebApiHandler_sendForOfflineSigning,
  useLeaseSigningWebApiHandler_sendForOnlineSigning,
  useLeaseTerminationWebApiHandler_getTerminationReasons,
  useLeaseTerminationWebApiHandler_terminateLease,
  useLeaseWebApiHandler_archiveLease,
  useLeaseWebApiHandler_createNewLease,
  useLeaseWebApiHandler_createNewLeaseForApplication,
  useLeaseWebApiHandler_getLease,
  useLeaseWizardWebApiHandler_getLeaseStatus,
  useLeaseWizardWebApiHandler_getLeaseStatusForNode,
  useLeaseWizardWebApiHandler_reevaluateLeaseCompletion,
  useLeaseWizardWebApiHandler_submitData,
  useLeasingCheckPaymentsWebApiHandler_checkActivePayments,
  useLeasingMessageWebApiHandler_getLastMessage,
  useLeasingPropertyOuidWebApiHandler_getPropertyInfoFromOuid,
  useLeasingPropertyWebApiHandler_getLeaseSupportForProperty,
  useLeasingPropertyWebApiHandler_getPaginatedLeasesForLandlordProperty,
  useLeasingPropertyWebApiHandler_getPropertyInfo,
  useLeasingTenantWebApiHandler_getPaginatedLeasesForTenant,
  useLeasingUserOuidWebApiHandler_getInfoForUser,
  useLeasingUserSigningOuidWebApiHandler_markAsSignedFromOuid,
  useLeasingUserSigningWebApiHandler_markAsSigned,
  useLeasingUserWebApiHandler_associateUser,
  useLeasingUserWebApiHandler_getLeasingUserPage,
  useLeasingUserWebApiHandler_verifyUserInfo,
  useLeasingUserWebApiHandler_verifyUserInfoForOuid,
  useMiniWorkflowQueueWebApiHandler_submitMiniWorkflowData,
  useResendSigningEmailsWebApiHandler_resendOnlineSigningEmails,
  useWaitlistWebApiHandler_addToWaitlist,
  useWaitlistWebApiHandler_declineToWaitlist,
  useWaitlistWebApiHandler_isOnWaitlist,
} from '@zg-rentals/ts-leasing-client-react-query';
import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/rental-manager-env';
import type { DatarouterError } from '@zg-rentals/rental-manager-env';

export const LEASING_WEB_API_PREFIX = 'rental/leasing/api/web/v1';

const UPLOADED_FILE_DETAILS = 'uploadedFileDetails';
const GET_LEASE_EXTENSION = 'leaseExtension';
const GET_LEASE_SUMMARY = 'leaseSummary';
const GET_RENTER_USER = 'renterUser';
const GET_LEASE_EXTENSION_FROM_OUID = 'leaseExtensionFromOuid';
const GET_LEASE_FROM_OUID = 'leaseFromOuid';
const GET_LEASE_SUMMARY_FROM_OUID = 'leaseSummaryFromOuid';
const WHO_HAS_SIGNED_FROM_OUID = 'whoHasSignedFromOuid';
const WHO_HAS_SIGNED = 'whoHasSigned';

export type QueryError = Error & {
  originalError?: unknown;
  userMessage?: string;
  actionText?: string;
  actionUrl?: string;
};

type QueryErrorContext = {
  userHeader: string;
  userMessage?: string;
  actionText?: string;
  actionUrl?: string;
};

function createQueryError(originalError: unknown, context: QueryErrorContext): QueryError {
  const error = new Error(context.userHeader) as QueryError;
  error.originalError = originalError;
  error.userMessage = context.userMessage;
  error.actionText = context.actionText;
  error.actionUrl = context.actionUrl;
  if (originalError instanceof Error) {
    error.stack = originalError.stack;
  }
  return error;
}

function isUserBlockedError(error: unknown): boolean {
  const errorDetails = (error as DatarouterError | null)?.errorDetails as { isUserBlocked?: boolean } | undefined;
  return errorDetails?.isUserBlocked === true;
}

const BLOCKED_USER_SUPPORT_URL =
  'https://www.zillow.com/rental-manager/submit-request?category=myAccount&topic=blockedAccount';

const BLOCKED_USER_ERROR: QueryErrorContext = {
  userHeader: 'Your Zillow Rental Manager account is disabled',
  userMessage: 'Contact our support team to resolve the issue.',
  actionText: 'Contact support',
  actionUrl: BLOCKED_USER_SUPPORT_URL,
};

export function useLeaseQuery(leaseId: number, options?: { enabled?: boolean }) {
  const query = useLeaseWebApiHandler_getLease(
    { leaseId },
    {
      ...options,
      enabled: options?.enabled !== false && !!leaseId,
      // @ts-expect-error - Generated hook types don't properly support select transformations
      select: (response) => new Lease(response.lease),
      meta: {
        tags: ['leasing:lease-status-change'],
      },
    },
  ) as unknown as ReturnType<typeof useLeaseWebApiHandler_getLease> & {
    data: Lease | undefined;
  };

  // Only throw if we actually tried to fetch and got an error (not just disabled)
  if (query.error && query.isFetched) {
    throw createQueryError(
      query.error,
      isUserBlockedError(query.error)
        ? BLOCKED_USER_ERROR
        : {
            userHeader: 'Could not load your lease',
            userMessage: 'You may not have access to this lease, or the lease may no longer exist.',
          },
    );
  }

  return query;
}

export function useUserInfoTokenQuery(leaseId: number, options?: { enabled?: boolean }) {
  return useLeaseWebApiHandler_getLease(
    { leaseId },
    {
      ...options,
      enabled: options?.enabled !== false && !!leaseId,
      // @ts-expect-error - Generated hook types don't properly support select transformations
      select: (response) => response.userInfoToken,
    },
  ) as unknown as ReturnType<typeof useLeaseWebApiHandler_getLease> & {
    data: string | undefined;
  };
}

export function usePropertyInfoQuery(propertyId: string, enabled = true) {
  const query = useLeasingPropertyWebApiHandler_getPropertyInfo({ propertyId }, { enabled: enabled && !!propertyId });

  // Throw if propertyId is missing but query was supposed to be enabled
  if (enabled && !propertyId) {
    throw createQueryError(new Error('Missing propertyId'), {
      userHeader: 'Could not load property information',
      userMessage: 'You may not have access to this property, or the property may no longer exist.',
    });
  }

  // Only throw if we actually tried to fetch and got an error
  if (query.error && query.isFetched) {
    throw createQueryError(
      query.error,
      isUserBlockedError(query.error)
        ? BLOCKED_USER_ERROR
        : {
            userHeader: 'Could not load property information',
            userMessage: 'You may not have access to this property, or the property may no longer exist.',
          },
    );
  }

  return query;
}

export function usePropertyInfoFromOuidQuery(ouid: string, enabled: boolean) {
  const query = useLeasingPropertyOuidWebApiHandler_getPropertyInfoFromOuid({ ouid }, { enabled: enabled && !!ouid });

  if (query.error && !query.isLoading && query.fetchStatus !== 'idle') {
    throw createQueryError(
      query.error,
      isUserBlockedError(query.error)
        ? BLOCKED_USER_ERROR
        : {
            userHeader: 'Could not load property information',
            userMessage: 'You may not have access to this property, or the property may no longer exist.',
          },
    );
  }

  return query;
}

export function useLastMessageQuery(leaseId: number, enabled = true) {
  return useLeasingMessageWebApiHandler_getLastMessage({ leaseId }, { enabled });
}

export function usePaginatedLeasesForTenant(
  params: { numResults: number; offset: number },
  options?: { enabled?: boolean },
) {
  return useLeasingTenantWebApiHandler_getPaginatedLeasesForTenant(params, {
    ...options,
    meta: {
      tags: ['leasing:lease-status-change'],
    },
  });
}

export function usePaginatedLeasesForLandlordQuery(
  params: { listingAlias: string; numResults: number; offset: number },
  options?: { enabled?: boolean },
) {
  return useLeasingPropertyWebApiHandler_getPaginatedLeasesForLandlordProperty(params, {
    ...options,
    meta: {
      tags: ['leasing:lease-status-change', 'leasing:lease-dashboard-change'],
    },
  });
}

export function usePropertiesInfoQueries(propertyIds: Array<string>) {
  const propertyQueries = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: useLeasingPropertyWebApiHandler_getPropertyInfo.getKey({ propertyId }),
      queryFn: () => LeasingPropertyWebApiHandler_getPropertyInfo({ propertyId }),
      enabled: !!propertyId,
    })),
  });

  const propertiesById = propertyQueries.reduce(
    (acc, query, index) => {
      const propertyId = propertyIds[index];
      if (query.data && propertyId) {
        const propertyData = { ...query.data, propertyId };
        acc[propertyId] = new Property(propertyData);
      }
      return acc;
    },
    {} as { [propertyId: string]: Property },
  );

  return { propertiesById, queries: propertyQueries };
}

export function useDeleteSelfUploadedDocumentMutation() {
  return useLeaseDocumentUploadWebApiHandler_deleteLeaseDocument({
    meta: {
      invalidates: ['leasing:lease-documents', 'leasing:lease-status-change'],
    },
  });
}

export function useUploadedFileDetailsQuery(leaseId: number) {
  return useQuery({
    queryKey: [UPLOADED_FILE_DETAILS, leaseId],
    queryFn: () => LeaseDocumentUploadWebApiHandler_getUploadedFileDetails({ leaseId }),
    select: (response) => response.documentDetails,
    enabled: !isNaN(leaseId) && leaseId > 0,
    meta: {
      tags: ['leasing:lease-documents'],
    },
  });
}

export function useDeleteLeaseDocumentMutation() {
  return useLeaseDocumentUploadWebApiHandler_deleteLeaseDocument({
    meta: {
      invalidates: ['leasing:lease-documents', 'leasing:lease-status-change'],
    },
  });
}

export function useRenameLeaseDocumentsMutation() {
  return useLeaseDocumentUploadWebApiHandler_renameLeaseDocuments({
    meta: {
      invalidates: ['leasing:lease-documents', 'leasing:lease-status-change'],
    },
  });
}

export function useReorderLeaseDocumentsMutation() {
  return useLeaseDocumentUploadWebApiHandler_reorderLeaseDocuments({});
}

export function useSubmitDocumentChangesMutation() {
  return useLeaseDocumentUploadWebApiHandler_submitDocumentChanges({
    meta: {
      invalidates: ['leasing:lease-documents', 'leasing:lease-status-change'],
    },
  });
}

export function useZDocsAccessTokenQuery(leaseId: number) {
  return useLeaseDocumentUploadWebApiHandler_getZDocsUploadToken(
    {
      leaseId,
    },
    {
      refetchOnMount: 'always',
    },
  );
}

export function useStoreDocumentIdMutation() {
  return useLeaseDocumentUploadWebApiHandler_storeDocumentId({
    meta: {
      invalidates: ['leasing:lease-documents'],
    },
  });
}

export function useCreateNewLeaseMutation() {
  return useLeaseWebApiHandler_createNewLease({
    meta: {
      invalidates: ['leasing:lease-dashboard-change'],
    },
  });
}

export function useCreateNewLeaseForApplicationMutation() {
  return useLeaseWebApiHandler_createNewLeaseForApplication({
    meta: {
      invalidates: ['leasing:lease-dashboard-change'],
    },
  });
}

export function useSaveGeneratedLeaseFeedbackMutation() {
  return useGeneratedLeaseWebApiHandler_saveFeedback();
}

export function useCreateLeaseRenewalMutation() {
  return useLeaseRenewalWebApiHandler_create({
    meta: {
      invalidates: ['leasing:lease-dashboard-change'],
    },
  });
}

export async function getCosmosJourneyToken(
  leaseArtifactType: LeaseArtifactType,
  leaseArtifactId: number,
): Promise<string> {
  const response = await LeasingGetCosmosJourneyTokenWebApiHandler_getJourneyToken({
    leaseArtifactType,
    leaseArtifactId,
  });

  return response.token;
}

export async function getCosmosJourneyTokenFromOuid(ouid: string): Promise<string> {
  const response = await LeasingGetCosmosJourneyTokenWebApiHandler_getJourneyTokenFromOuid({ ouid });

  return response.token;
}

export function useLeaseExtensionQuery(leaseExtensionId: number) {
  return useQuery({
    queryKey: [GET_LEASE_EXTENSION, leaseExtensionId],
    queryFn: () =>
      LeaseRenewalWebApiHandler_getLeaseExtension({
        leaseExtensionId: leaseExtensionId,
      }),
    select: (data) => data.leaseExtension,
    meta: {
      tags: ['leasing:lease-extension-status-change'],
    },
  });
}

export function usePreviewUrlQuery(
  leaseArtifactId: number,
  leaseArtifactType: LeaseArtifactType,
  enabled = true,
  excludeAttachments = false,
) {
  return useLeaseDocumentsWebApiHandler_previewUrl(
    { leaseArtifactId, leaseArtifactType, excludeAttachments },
    {
      enabled,
      refetchOnMount: 'always',
      meta: {
        tags: ['leasing:lease-preview'],
      },
    },
  );
}

export function useLeaseSummaryQuery(
  leaseArtifactId: number,
  leaseArtifactType: LeaseArtifactType,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [GET_LEASE_SUMMARY, leaseArtifactId, leaseArtifactType],
    refetchOnMount: 'always',
    queryFn: () => LeaseSummaryWebApiHandler_getLeaseSummary({ leaseArtifactId, leaseArtifactType }),
    select: (data) => {
      return {
        documentActions: data.documentActions.map((action) => new LeaseAction(action.name, action.disabled)),
        summaryActions: data.summaryActions.map((action) => new LeaseAction(action.name, action.disabled)),
        percentCompletion: data.percentCompletion,
        summary: data.summary,
        summaryText: data.summaryText,
      };
    },
    meta: {
      tags: ['leasing:lease-status-change'],
    },
    enabled: options?.enabled !== false,
  });
}

export function useReevaluateLeaseCompletionMutation() {
  return useLeaseWizardWebApiHandler_reevaluateLeaseCompletion({
    meta: {
      invalidates: ['leasing:lease-status-change'],
    },
  });
}

export function useSendForOnlineSigningMutation() {
  return useLeaseSigningWebApiHandler_sendForOnlineSigning({
    meta: {
      invalidates: [
        'leasing:lease-status-change',
        'leasing:lease-extension-status-change',
        'leasing:lease-dashboard-change',
        'leasing:lease-preview',
      ],
    },
  });
}

export function useSendForOfflineSigningMutation() {
  return useLeaseSigningWebApiHandler_sendForOfflineSigning({
    meta: {
      invalidates: [
        'leasing:lease-status-change',
        'leasing:lease-extension-status-change',
        'leasing:lease-dashboard-change',
        'leasing:lease-preview',
      ],
    },
  });
}

export function useMarkAsSignedMutation() {
  return useLeasingUserSigningWebApiHandler_markAsSigned({
    meta: {
      invalidates: ['leasing:lease-status-change', 'leasing:lease-dashboard-change', 'leasing:disclaimer-acceptance'],
    },
  });
}

export function useCloneLeaseMutation() {
  return useCloneLeaseWebApiHandler_cloneLease({
    meta: {
      invalidates: ['leasing:lease-dashboard-change'],
    },
  });
}

export function useTerminationReasonsQuery() {
  return useLeaseTerminationWebApiHandler_getTerminationReasons();
}

export function useTerminateLeaseMutation() {
  return useLeaseTerminationWebApiHandler_terminateLease({
    meta: {
      invalidates: ['leasing:lease-status-change', 'leasing:lease-dashboard-change'],
    },
  });
}

export function useCancelSigningMutation() {
  return useLeaseSigningWebApiHandler_cancelSigning({
    meta: {
      invalidates: [
        'leasing:lease-status-change',
        'leasing:lease-extension-status-change',
        'leasing:lease-dashboard-change',
        'leasing:lease-preview',
      ],
    },
  });
}

export function useRenterUser() {
  return useQuery({
    queryKey: [GET_RENTER_USER],
    refetchOnMount: 'always',
    queryFn: () => getRenterUser(),
    select: (data) => data.response,
  });
}

async function getRenterUser() {
  const res = await fetch(`${baseUrl}/renter-hub-api/api/v1/users/get`, {
    credentials: 'include',
  });
  return res.json();
}

export function useInfoForUser(ouid: string, enabled: boolean) {
  return useLeasingUserOuidWebApiHandler_getInfoForUser({ ouid }, { enabled });
}

export function useLeaseExtensionFromOuidQuery(ouid: string, enabled: boolean) {
  return useQuery({
    queryKey: [GET_LEASE_EXTENSION_FROM_OUID, ouid],
    enabled: enabled && !!ouid,
    queryFn: () => LeaseRenewalOuidWebApiHandler_getLeaseExtensionFromOuid({ ouid }),
    select: (data) => data.leaseExtension,
    meta: {
      tags: ['leasing:lease-extension-status-change'],
    },
  });
}

export function useLeaseFromOuidQuery(ouid: string, enabled: boolean) {
  const query = useQuery({
    queryKey: [GET_LEASE_FROM_OUID, ouid],
    enabled: enabled && !!ouid,
    queryFn: () => LeaseOuidWebApiHandler_getLeaseFromOuid({ ouid }),
    select: (response) => new Lease(response.lease),
    meta: {
      tags: ['leasing:lease-status-change'],
    },
  });

  // Only throw if we actually tried to fetch and got an error (not just loading or disabled)
  if (query.error && !query.isLoading && query.fetchStatus !== 'idle') {
    throw createQueryError(
      query.error,
      isUserBlockedError(query.error)
        ? BLOCKED_USER_ERROR
        : {
            userHeader: 'Could not load your lease',
            userMessage: 'You may not have access to this lease, or the lease may no longer exist.',
          },
    );
  }

  return query;
}

export function useLeaseSummaryFromOuidQuery(ouid: string, enabled: boolean) {
  return useQuery({
    queryKey: [GET_LEASE_SUMMARY_FROM_OUID, ouid],
    enabled: enabled && !!ouid,
    queryFn: () => LeaseSummaryOuidWebApiHandler_getLeaseSummaryFromOuid({ ouid }),
    select: (data) => {
      const { documentActions, summaryActions } = data;
      const dataClone = (({ ...rest }) => rest)(data);

      const mappedDocumentActions = documentActions.map((action) => new LeaseAction(action.name, action.disabled));
      const mappedSummarryActions = summaryActions.map((action) => new LeaseAction(action.name, action.disabled));

      return { ...dataClone, documentActions: mappedDocumentActions, summaryActions: mappedSummarryActions };
    },
    meta: {
      tags: ['leasing:lease-status-change'],
    },
  });
}

export function usePreviewUrlFromOuidQuery(ouid: string, enabled: boolean) {
  return useLeaseDocumentsOuidWebApiHandler_previewUrlFromOuid(
    { ouid },
    {
      enabled: enabled && !!ouid,
      refetchOnMount: 'always',
      meta: {
        tags: ['leasing:lease-preview'],
      },
    },
  );
}

export function useWhoHasSignedFromOuidQuery(ouid: string, enabled: boolean) {
  return useQuery({
    queryKey: [WHO_HAS_SIGNED_FROM_OUID, ouid],
    enabled: enabled && !!ouid,
    queryFn: () => LeasingUserSigningOuidWebApiHandler_whoHasSignedFromOuid({ ouid }),
    select: (data) => data.signedUsers,
    meta: {
      tags: ['leasing:lease-status-change'],
    },
  });
}

export function useMarkAsSignedFromOuidMutation() {
  // Edge case for when a tenant is signed in, but the colandlord uses their link from this account.
  return useLeasingUserSigningOuidWebApiHandler_markAsSignedFromOuid({
    meta: {
      invalidates: ['leasing:lease-status-change', 'leasing:lease-dashboard-change', 'leasing:disclaimer-acceptance'],
    },
  });
}

export function useWhoHasSignedQuery(leaseArtifactId: number, leaseArtifactType: LeaseArtifactType, enabled = true) {
  return useQuery({
    queryKey: [WHO_HAS_SIGNED, leaseArtifactId, leaseArtifactType],
    enabled,
    queryFn: () =>
      LeasingUserSigningWebApiHandler_whoHasSigned({
        leaseArtifactId: leaseArtifactId,
        leaseArtifactType: leaseArtifactType,
      }),
    select: (data) => data.signedUsers,
    meta: {
      tags: ['leasing:lease-status-change'],
    },
  });
}

export function useArchiveLeaseMutation() {
  return useLeaseWebApiHandler_archiveLease({
    meta: {
      invalidates: [
        'leasing:lease-status-change',
        'leasing:lease-extension-status-change',
        'leasing:lease-dashboard-change',
      ],
    },
  });
}

export function useResendSigningEmailsMutation() {
  return useResendSigningEmailsWebApiHandler_resendOnlineSigningEmails();
}

export function useLeaseSupportForPropertyQuery(listingAlias: string) {
  return useLeasingPropertyWebApiHandler_getLeaseSupportForProperty({ propertyId: listingAlias });
}

export function useAssociateUserMutation() {
  return useLeasingUserWebApiHandler_associateUser();
}

export function useWebAnalyticsForListingQuery(listingAlias: string, options?: { enabled: boolean }) {
  return useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    { listingAlias },
    {
      ...options,
    },
  );
}

export function useCompletionStatusQuery(leaseId: number, options?: { enabled?: boolean }) {
  return useLeaseCompletionWebApiHandler_getCompletionStatus(
    { leaseId },
    {
      enabled: options?.enabled !== false && !!leaseId,
    },
  );
}

export function useApplicantsForApplicationQuery(
  propertyId: string,
  applicationId: string,
  options?: { enabled?: boolean },
) {
  return useApplicantsWebApiHandler_getApplicantsForPropertyApplicationId(
    {
      listingAlias: propertyId,
      applicationId,
    },
    {
      enabled: options?.enabled !== false && !!propertyId && !!applicationId,
    },
  );
}

export function useLandlordProvidedDocumentsQuery(leaseId: number, options?: { enabled?: boolean }) {
  return useLeaseDocumentsWebApiHandler_getLandlordProvidedDocuments(
    { leaseId },
    {
      enabled: options?.enabled !== false && !!leaseId,
      meta: {
        tags: ['leasing:lease-documents'],
      },
    },
  );
}

export function useLandlordProvidedDocumentsFromOuidQuery(ouid: string, enabled: boolean) {
  return useLeaseDocumentsOuidWebApiHandler_getLandlordProvidedDocumentsFromOuid(
    { ouid },
    {
      enabled: enabled && !!ouid,
      meta: {
        tags: ['leasing:lease-documents'],
      },
    },
  );
}

export function useDisclaimerAcceptanceForUserQuery(leaseId: number, options?: { enabled?: boolean }) {
  return useLeaseDisclaimerAcceptanceWebApiHandler_getDisclaimerAcceptanceForUser(
    { leaseId },
    {
      enabled: options?.enabled !== false && !isNaN(leaseId) && leaseId > 0,
    },
  );
}

export function useAttachedLeaseAcceptanceForUserQuery(leaseId: number, options?: { enabled?: boolean }) {
  return useLeaseDisclaimerAcceptanceWebApiHandler_getAttachedLeaseAcceptanceForUser(
    { leaseId },
    {
      enabled: options?.enabled !== false && !isNaN(leaseId) && leaseId > 0,
      meta: {
        tags: ['leasing:disclaimer-acceptance'],
      },
    },
  );
}

export function useSetAttachedLeaseAcceptanceForUserMutation() {
  return useLeaseDisclaimerAcceptanceWebApiHandler_setAttachedLeaseAcceptanceForUser();
}

export function useSetDisclaimerAcceptanceForUserMutation() {
  return useLeaseDisclaimerAcceptanceWebApiHandler_setDisclaimerAcceptanceForUser();
}

// OUID-based disclaimer acceptance queries for colandlords
export function useDisclaimerAcceptanceForUserFromOuidQuery(ouid: string, enabled: boolean) {
  return useLeaseDisclaimerAcceptanceFromOuidWebApiHandler_getDisclaimerAcceptanceForUserFromOuid(
    { ouid },
    {
      enabled: enabled && !!ouid,
      meta: {
        tags: ['leasing:disclaimer-acceptance'],
      },
    },
  );
}

export function useSetDisclaimerAcceptanceForUserFromOuidMutation() {
  return useLeaseDisclaimerAcceptanceFromOuidWebApiHandler_setDisclaimerAcceptanceForUserFromOuid({
    meta: {
      invalidates: ['leasing:disclaimer-acceptance'],
    },
  });
}

export function useSetAttachedLeaseAcceptanceForUserFromOuidMutation() {
  return useLeaseDisclaimerAcceptanceFromOuidWebApiHandler_setAttachedLeaseAcceptanceForUserFromOuid({
    meta: {
      invalidates: ['leasing:disclaimer-acceptance'],
    },
  });
}

export function useAttachedLeaseAcceptanceForUserFromOuidQuery(ouid: string, enabled: boolean) {
  return useLeaseDisclaimerAcceptanceFromOuidWebApiHandler_getAttachedLeaseAcceptanceForUserFromOuid(
    { ouid },
    {
      enabled,
      meta: {
        tags: ['leasing:disclaimer-acceptance'],
      },
    },
  );
}

export function useExplainerLeaseFromOuidQuery(ouid: string, enabled: boolean) {
  return useLeaseDocumentsOuidWebApiHandler_getExplainerLeaseFromOuid(
    { ouid },
    {
      enabled,
    },
  );
}

export function useExplainerLeaseForLeaseQuery(leaseId: number, enabled: boolean) {
  return useLeaseDocumentsWebApiHandler_getExplainerLeaseForLease(
    { leaseId },
    {
      enabled,
    },
  );
}

export function useVerifyUserInfoQuery(verifyUserInfoToken: string, enabled: boolean) {
  return useLeasingUserWebApiHandler_verifyUserInfo({ verifyUserInfoToken }, { enabled, retry: false });
}

export function useVerifyUserInfoForOuidQuery(ouid: string, verifyUserInfoToken: string, enabled: boolean) {
  return useLeasingUserWebApiHandler_verifyUserInfoForOuid({ ouid, verifyUserInfoToken }, { enabled, retry: false });
}

export function useIsOnWaitlistQuery(propertyId: string) {
  return useWaitlistWebApiHandler_isOnWaitlist(
    { propertyId },
    {
      meta: {
        tags: ['leasing:waitlist'],
      },
    },
  );
}

export function useAddToWaitlistMutation() {
  return useWaitlistWebApiHandler_addToWaitlist({
    meta: {
      invalidates: ['leasing:waitlist'],
    },
  });
}

export function useDeclineToWaitlistMutation() {
  return useWaitlistWebApiHandler_declineToWaitlist({
    meta: {
      invalidates: ['leasing:waitlist'],
    },
  });
}

export function useLeasingStatusQuery(leaseId: number, options?: { enabled?: boolean }) {
  const query = useLeaseWizardWebApiHandler_getLeaseStatus(
    { leaseId },
    {
      ...options,
      meta: {
        tags: ['leasing:wizard-section', 'leasing:lease-status-change'],
      },
      staleTime: 0,
      gcTime: 0, // Don't cache section data as it may change based on other selections
    },
  );

  const sectionData = useMemo(() => (query.data ? new Section(query.data) : undefined), [query.data]);

  return {
    ...query,
    data: sectionData,
  };
}

export function useLeasingStatusForNodeQuery(nodeId: number, leaseId: number, options?: { enabled?: boolean }) {
  const query = useLeaseWizardWebApiHandler_getLeaseStatusForNode(
    { nodeId, leaseId },
    {
      ...options,
      meta: {
        tags: ['leasing:wizard-section', 'leasing:lease-status-change'],
      },
      staleTime: 0,
      gcTime: 0, // Don't cache section data as it may change based on other selections
    },
  );

  const sectionData = useMemo(() => (query.data ? new Section(query.data) : undefined), [query.data]);

  return {
    ...query,
    data: sectionData,
  };
}

export function useLeasingUserPageQuery(leaseId: number, options?: { enabled?: boolean }) {
  return useLeasingUserWebApiHandler_getLeasingUserPage({ leaseId }, options);
}

export function useSubmitWizardDataMutation() {
  return useLeaseWizardWebApiHandler_submitData({
    meta: {
      invalidates: ['leasing:wizard-section', 'leasing:lease-status-change'],
    },
  });
}

export function useSubmitMiniWorkflowDataMutation() {
  return useMiniWorkflowQueueWebApiHandler_submitMiniWorkflowData({
    meta: {
      invalidates: ['leasing:wizard-section', 'leasing:lease-status-change'],
    },
  });
}

export function useGetFirstNodeInSectionCategoryMutation() {
  return useMutation({
    mutationFn: (params: { leaseId: number; sectionCategory: string }) =>
      LeaseWizardWebApiHandler_getFirstNodeInSectionCategory(params),
  });
}

export function useGetNextNodeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { currentNodeId: number; leaseId: number }) => LeaseWizardWebApiHandler_getNextNode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.meta?.tags?.includes('leasing:wizard-section'),
      });
    },
  });
}

export function useGetPreviousNodeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { currentNodeId: number; leaseId: number }) =>
      LeaseWizardWebApiHandler_getPreviousNode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.meta?.tags?.includes('leasing:wizard-section'),
      });
    },
  });
}

export function useCheckActivePaymentsQuery(leaseId: number, options?: { enabled?: boolean }) {
  return useLeasingCheckPaymentsWebApiHandler_checkActivePayments({ leaseId }, options);
}
